import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import SuccessToast from '../../../components/ui/SuccessToast';

const VEHICLE_TYPES = ['sedan', 'suv', 'pickup', 'van', 'bus', 'truck', 'motorcycle', 'tuk_tuk'];
const TRANSMISSIONS = ['automatic', 'manual'];
const FUELS = ['petrol', 'diesel', 'hybrid', 'electric'];
const RENTAL_MODES = [
  { value: 'self_drive', label: 'Self-Drive Only' },
  { value: 'chauffeur', label: 'Chauffeur Only' },
  { value: 'both', label: 'Both Options' },
];

const AdminVehicleUpload = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', subMessage: '', variant: 'success' });
  const [errors, setErrors] = useState({});

  const defaultForm = {
    make: '', model: '', year: new Date()?.getFullYear(), color: '', seats: 5,
    transmission: 'automatic', fuel: 'petrol', vehicle_type: 'sedan',
    rental_mode: 'self_drive', location_address: '', description: '',
    base_daily_rate: '', deposit_amount: '', weekend_multiplier: 1.2,
    min_rental_days: 1, max_rental_days: 30, min_driver_age: 23,
    status: 'active', owner_email: '',
  };
  const [form, setForm] = useState(defaultForm);

  const showToast = (message, subMessage, variant = 'success') => {
    setToast({ visible: true, message, subMessage, variant });
  };

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors?.[field]) setErrors(e => ({ ...e, [field]: null }));
  };

  const validate = () => {
    const errs = {};

    // Required text fields
    if (!form?.make?.trim()) errs.make = 'Make is required';
    if (!form?.model?.trim()) errs.model = 'Model is required';
    if (!form?.color?.trim()) errs.color = 'Color is required';
    if (!form?.location_address?.trim()) errs.location_address = 'Pickup location is required';

    // Year range
    const currentYear = new Date()?.getFullYear();
    const year = parseInt(form?.year);
    if (!form?.year) {
      errs.year = 'Year is required';
    } else if (isNaN(year) || year < 1990 || year > currentYear + 1) {
      errs.year = `Year must be between 1990 and ${currentYear + 1}`;
    }

    // Seats range
    const seats = parseInt(form?.seats);
    if (!form?.seats) {
      errs.seats = 'Seats is required';
    } else if (isNaN(seats) || seats < 1 || seats > 60) {
      errs.seats = 'Seats must be between 1 and 60';
    }

    // Selects — ensure non-empty
    if (!form?.vehicle_type) errs.vehicle_type = 'Vehicle type is required';
    if (!form?.transmission) errs.transmission = 'Transmission is required';
    if (!form?.fuel) errs.fuel = 'Fuel type is required';
    if (!form?.rental_mode) errs.rental_mode = 'Rental mode is required';

    // Pricing — required + range
    const dailyRate = parseFloat(form?.base_daily_rate);
    if (!form?.base_daily_rate && form?.base_daily_rate !== 0) {
      errs.base_daily_rate = 'Daily rate is required';
    } else if (isNaN(dailyRate) || dailyRate < 500) {
      errs.base_daily_rate = 'Daily rate must be at least KES 500';
    } else if (dailyRate > 500000) {
      errs.base_daily_rate = 'Daily rate cannot exceed KES 500,000';
    }

    const deposit = parseFloat(form?.deposit_amount);
    if (!form?.deposit_amount && form?.deposit_amount !== 0) {
      errs.deposit_amount = 'Security deposit is required';
    } else if (isNaN(deposit) || deposit < 1000) {
      errs.deposit_amount = 'Deposit must be at least KES 1,000';
    } else if (deposit > 2000000) {
      errs.deposit_amount = 'Deposit cannot exceed KES 2,000,000';
    }

    const multiplier = parseFloat(form?.weekend_multiplier);
    if (isNaN(multiplier) || multiplier < 1.0) {
      errs.weekend_multiplier = 'Multiplier must be at least 1.0';
    } else if (multiplier > 3.0) {
      errs.weekend_multiplier = 'Multiplier cannot exceed 3.0';
    }

    // Rental days logic
    const minDays = parseInt(form?.min_rental_days);
    const maxDays = parseInt(form?.max_rental_days);
    if (isNaN(minDays) || minDays < 1) {
      errs.min_rental_days = 'Min rental days must be at least 1';
    }
    if (isNaN(maxDays) || maxDays < 1) {
      errs.max_rental_days = 'Max rental days must be at least 1';
    }
    if (!errs?.min_rental_days && !errs?.max_rental_days && minDays > maxDays) {
      errs.max_rental_days = 'Max days must be greater than or equal to min days';
    }

    // Min driver age
    const minAge = parseInt(form?.min_driver_age);
    if (isNaN(minAge) || minAge < 18) {
      errs.min_driver_age = 'Minimum driver age must be at least 18';
    } else if (minAge > 70) {
      errs.min_driver_age = 'Minimum driver age cannot exceed 70';
    }

    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Resolve owner_id: if email provided, look up user_profiles; else use current admin
      let ownerId = user?.id;
      if (form?.owner_email?.trim()) {
        const { data: profileData } = await supabase
          ?.from('user_profiles')
          ?.select('id')
          ?.eq('email', form?.owner_email?.trim())
          ?.single();
        if (profileData?.id) ownerId = profileData?.id;
      }

      const { data: vehicleData, error: vehicleError } = await supabase
        ?.from('vehicles')
        ?.insert({
          owner_id: ownerId,
          title: `${form?.make} ${form?.model} ${form?.year}`,
          make: form?.make,
          model: form?.model,
          year: parseInt(form?.year),
          vehicle_type: form?.vehicle_type,
          seats: parseInt(form?.seats),
          transmission: form?.transmission,
          fuel: form?.fuel,
          color: form?.color,
          location_address: form?.location_address,
          rental_mode: form?.rental_mode,
          status: form?.status,
          description: form?.description,
          min_rental_days: parseInt(form?.min_rental_days),
          max_rental_days: parseInt(form?.max_rental_days),
          min_driver_age: parseInt(form?.min_driver_age),
          deposit_required: true,
        })
        ?.select()
        ?.single();

      if (vehicleError) throw vehicleError;

      // Create pricing record
      const { error: pricingError } = await supabase
        ?.from('vehicle_pricing')
        ?.insert({
          vehicle_id: vehicleData?.id,
          base_daily_rate: parseFloat(form?.base_daily_rate),
          weekend_multiplier: parseFloat(form?.weekend_multiplier),
          deposit_amount: parseFloat(form?.deposit_amount),
          min_days: parseInt(form?.min_rental_days),
        });

      if (pricingError) console.error('Pricing insert error:', pricingError);

      showToast(
        `${form?.make} ${form?.model} ${form?.year} added`,
        `Status: ${form?.status === 'active' ? 'Live on platform' : 'Pending review'}`,
        'success'
      );
      setForm(defaultForm);
      setIsOpen(false);
    } catch (e) {
      console.error(e);
      showToast('Upload failed', e?.message || 'Please try again', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (err) =>
    `w-full px-3 py-2 rounded-lg text-text-primary placeholder-text-secondary text-sm outline-none focus:ring-1 focus:ring-accent bg-surface-hover border ${err ? 'border-error' : 'border-border'}`;
  const labelCls = "text-text-secondary text-xs font-semibold uppercase tracking-wider mb-1 block";

  return (
    <div className="bg-surface rounded-xl border border-border p-5">
      <SuccessToast
        visible={toast?.visible}
        message={toast?.message}
        subMessage={toast?.subMessage}
        onDismiss={() => setToast(t => ({ ...t, visible: false }))}
        duration={4000}
        position="top"
        variant={toast?.variant}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
            <Icon name="PlusCircle" size={18} style={{ color: '#818cf8' }} />
          </div>
          <div>
            <h3 className="text-text-primary font-bold text-base">Admin Vehicle Upload</h3>
            <p className="text-text-secondary text-xs">Add a vehicle directly to the platform</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(o => !o)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
          style={{ background: isOpen ? 'rgba(248,113,113,0.12)' : 'rgba(99,102,241,0.15)', color: isOpen ? '#f87171' : '#818cf8', border: `1px solid ${isOpen ? 'rgba(248,113,113,0.3)' : 'rgba(99,102,241,0.3)'}` }}
        >
          <Icon name={isOpen ? 'ChevronUp' : 'Plus'} size={15} />
          {isOpen ? 'Close Form' : 'Add Vehicle'}
        </button>
      </div>

      {isOpen && (
        <div className="space-y-5 border-t border-border pt-5">
          {/* Owner lookup */}
          <div>
            <label className={labelCls}>Owner Email (optional — leave blank to assign to admin)</label>
            <input
              className={inputCls()}
              placeholder="owner@example.com"
              value={form?.owner_email}
              onChange={e => set('owner_email', e?.target?.value)}
            />
            <p className="text-text-secondary text-xs mt-1">If the owner has an account, enter their email to link the vehicle to them.</p>
          </div>

          {/* Vehicle basics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Make *</label>
              <input className={inputCls(errors?.make)} placeholder="Toyota" value={form?.make} onChange={e => set('make', e?.target?.value)} />
              {errors?.make && <p className="text-error text-xs mt-0.5">{errors?.make}</p>}
            </div>
            <div>
              <label className={labelCls}>Model *</label>
              <input className={inputCls(errors?.model)} placeholder="Prado" value={form?.model} onChange={e => set('model', e?.target?.value)} />
              {errors?.model && <p className="text-error text-xs mt-0.5">{errors?.model}</p>}
            </div>
            <div>
              <label className={labelCls}>Year *</label>
              <input type="number" className={inputCls(errors?.year)} value={form?.year} onChange={e => set('year', e?.target?.value)} />
              {errors?.year && <p className="text-error text-xs mt-0.5">{errors?.year}</p>}
            </div>
            <div>
              <label className={labelCls}>Color *</label>
              <input className={inputCls(errors?.color)} placeholder="White" value={form?.color} onChange={e => set('color', e?.target?.value)} />
              {errors?.color && <p className="text-error text-xs mt-0.5">{errors?.color}</p>}
            </div>
            <div>
              <label className={labelCls}>Seats *</label>
              <input type="number" min={1} max={60} className={inputCls(errors?.seats)} value={form?.seats} onChange={e => set('seats', e?.target?.value)} />
              {errors?.seats && <p className="text-error text-xs mt-0.5">{errors?.seats}</p>}
            </div>
            <div>
              <label className={labelCls}>Vehicle Type *</label>
              <select className={inputCls(errors?.vehicle_type)} value={form?.vehicle_type} onChange={e => set('vehicle_type', e?.target?.value)}>
                {VEHICLE_TYPES?.map(t => <option key={t} value={t}>{t?.replace('_', ' ')}</option>)}
              </select>
              {errors?.vehicle_type && <p className="text-error text-xs mt-0.5">{errors?.vehicle_type}</p>}
            </div>
            <div>
              <label className={labelCls}>Transmission *</label>
              <select className={inputCls(errors?.transmission)} value={form?.transmission} onChange={e => set('transmission', e?.target?.value)}>
                {TRANSMISSIONS?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {errors?.transmission && <p className="text-error text-xs mt-0.5">{errors?.transmission}</p>}
            </div>
            <div>
              <label className={labelCls}>Fuel *</label>
              <select className={inputCls(errors?.fuel)} value={form?.fuel} onChange={e => set('fuel', e?.target?.value)}>
                {FUELS?.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              {errors?.fuel && <p className="text-error text-xs mt-0.5">{errors?.fuel}</p>}
            </div>
            <div>
              <label className={labelCls}>Rental Mode *</label>
              <select className={inputCls(errors?.rental_mode)} value={form?.rental_mode} onChange={e => set('rental_mode', e?.target?.value)}>
                {RENTAL_MODES?.map(m => <option key={m?.value} value={m?.value}>{m?.label}</option>)}
              </select>
              {errors?.rental_mode && <p className="text-error text-xs mt-0.5">{errors?.rental_mode}</p>}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className={labelCls}>Pickup Location *</label>
            <input className={inputCls(errors?.location_address)} placeholder="Westlands, Nairobi" value={form?.location_address} onChange={e => set('location_address', e?.target?.value)} />
            {errors?.location_address && <p className="text-error text-xs mt-0.5">{errors?.location_address}</p>}
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Description</label>
            <textarea rows={2} className={inputCls()} placeholder="Brief description of the vehicle..." value={form?.description} onChange={e => set('description', e?.target?.value)} />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Daily Rate (KES) *</label>
              <input type="number" className={inputCls(errors?.base_daily_rate)} placeholder="5000" value={form?.base_daily_rate} onChange={e => set('base_daily_rate', e?.target?.value)} />
              {errors?.base_daily_rate && <p className="text-error text-xs mt-0.5">{errors?.base_daily_rate}</p>}
              {!errors?.base_daily_rate && <p className="text-text-secondary text-xs mt-0.5">Range: KES 500 – 500,000</p>}
            </div>
            <div>
              <label className={labelCls}>Security Deposit (KES) *</label>
              <input type="number" className={inputCls(errors?.deposit_amount)} placeholder="15000" value={form?.deposit_amount} onChange={e => set('deposit_amount', e?.target?.value)} />
              {errors?.deposit_amount && <p className="text-error text-xs mt-0.5">{errors?.deposit_amount}</p>}
              {!errors?.deposit_amount && <p className="text-text-secondary text-xs mt-0.5">Range: KES 1,000 – 2,000,000</p>}
            </div>
            <div>
              <label className={labelCls}>Weekend Multiplier</label>
              <input type="number" step="0.1" min="1" max="3" className={inputCls(errors?.weekend_multiplier)} value={form?.weekend_multiplier} onChange={e => set('weekend_multiplier', e?.target?.value)} />
              {errors?.weekend_multiplier && <p className="text-error text-xs mt-0.5">{errors?.weekend_multiplier}</p>}
              {!errors?.weekend_multiplier && <p className="text-text-secondary text-xs mt-0.5">Range: 1.0 – 3.0</p>}
            </div>
            <div>
              <label className={labelCls}>Min Rental Days</label>
              <input type="number" min={1} className={inputCls(errors?.min_rental_days)} value={form?.min_rental_days} onChange={e => set('min_rental_days', e?.target?.value)} />
              {errors?.min_rental_days && <p className="text-error text-xs mt-0.5">{errors?.min_rental_days}</p>}
            </div>
            <div>
              <label className={labelCls}>Max Rental Days</label>
              <input type="number" min={1} className={inputCls(errors?.max_rental_days)} value={form?.max_rental_days} onChange={e => set('max_rental_days', e?.target?.value)} />
              {errors?.max_rental_days && <p className="text-error text-xs mt-0.5">{errors?.max_rental_days}</p>}
            </div>
            <div>
              <label className={labelCls}>Min Driver Age</label>
              <input type="number" min={18} max={70} className={inputCls(errors?.min_driver_age)} value={form?.min_driver_age} onChange={e => set('min_driver_age', e?.target?.value)} />
              {errors?.min_driver_age && <p className="text-error text-xs mt-0.5">{errors?.min_driver_age}</p>}
              {!errors?.min_driver_age && <p className="text-text-secondary text-xs mt-0.5">Range: 18 – 70 years</p>}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className={labelCls}>Initial Status</label>
            <div className="flex gap-3">
              {[
                { value: 'active', label: 'Active (Live)', color: '#34d399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)' },
                { value: 'pending_review', label: 'Pending Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.3)' },
              ]?.map(opt => (
                <button
                  key={opt?.value}
                  type="button"
                  onClick={() => set('status', opt?.value)}
                  className="flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all"
                  style={{
                    background: form?.status === opt?.value ? opt?.bg : 'transparent',
                    color: form?.status === opt?.value ? opt?.color : 'var(--color-text-secondary)',
                    border: `1px solid ${form?.status === opt?.value ? opt?.border : 'var(--color-border)'}`,
                  }}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
            <p className="text-text-secondary text-xs mt-1">
              {form?.status === 'active' ? '✅ Vehicle will be immediately visible to customers.' : '⏳ Vehicle will enter the review queue before going live.'}
            </p>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { setForm(defaultForm); setErrors({}); setIsOpen(false); }}
              className="btn-secondary flex-1 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'rgba(99,102,241,0.2)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.4)' }}
            >
              {submitting ? (
                <><Icon name="Loader" size={15} className="animate-spin" />Adding Vehicle...</>
              ) : (
                <><Icon name="PlusCircle" size={15} />Add Vehicle to Platform</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVehicleUpload;
